import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface TableReportDetail {
    approvedByUserId: string | null;
    approvedByUserNm: string | null;
    approvedDatetime: string | null;
    claimedByUserId: string | null;
    claimedByUserNm: string | null;
    createdDatetime: string | null;
    directReportOnly: boolean;
    endTime: string | null;
    grabbedDatetime: string | null;
    postedByUserId: string | null;
    postedByUserNm: string | null;
    rejectedDatetime: string | null;
    rejectionNotes: string | null;
    requestDt: string;
    requestId: number;
    requestNotes: string | null;
    requestSeqId: number;
    requestStatus: string | null;
    requestType: string | null;
    requiredSkill: string | null;
    requiredTeam: string | null;
    RequiredTeamCodes: string[] | null;
    shiftId: number | null;
    shiftDayId: number | null;  
    startTime: string | null;
}

interface TableReportState {
    tableData: TableReportDetail[];
    loading: boolean;
    error: string | null;
}

const initialState: TableReportState = {
    tableData: [],
    loading: false,
    error: null,
};

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch table report data for a site with optional date range and status filter
 * Uses: ENDPOINTS.GET_TABLE_REPORT + siteCd + optional startDate/endDate + statusFilter
 */
export const fetchTableReport = createAsyncThunk(
    'tableReport/fetchTableReport',
    async (
        { siteCd, startDate, endDate, statusFilter }:
            { siteCd: string; startDate?: string; endDate?: string; statusFilter: string },
        { rejectWithValue }
    ) => {
        try {
            const params: string[] = [`siteCd=${siteCd}`, `statusFilter=${statusFilter}`];
            if (startDate) params.push(`startDate=${startDate}`);
            if (endDate) params.push(`endDate=${endDate}`);

            const url = `${ENDPOINTS.GET_TABLE_REPORT}?${params.join('&')}`;
            const response = await API_FETCH(url);
            console.log('✅ Table Report API Response:', response);
            return response;
        } catch (error: any) {
            console.error('❌ Table Report API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch table report data');
        }
    }
);

// ========================================
// SLICE
// ========================================
const tableReportSlice = createSlice({
    name: 'tableReport',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTableReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTableReport.fulfilled, (state, action) => {
                state.loading = false;
                state.tableData = action.payload;
            })
            .addCase(fetchTableReport.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = tableReportSlice.actions;
export default tableReportSlice.reducer;